import type { CoreMessage } from 'ai';
import type { LogService } from './LogService';

export interface PromptInterpolationOptions {
    /**
     * The user object with flattened data (table columns + JSONB fields merged)
     * This matches the ApiChatUser type from Public API v1
     * Can be any user-like object with an id and optional data field
     */
    user: {
        id?: number;
        data?: Record<string, any> | null;
        [key: string]: any;
    };

    /**
     * Optional chat messages for {{chatHistory}} placeholder
     * If not provided and {{chatHistory}} is used, will show "[Chat history not available]"
     */
    chatMessages?: CoreMessage[];

    /**
     * Optional log service for warning messages when placeholders are not found
     */
    log?: LogService;

    userId?: number;

}

function formatChatHistory(messages: CoreMessage[]): string {
    if (!messages || messages.length === 0) {
        return '[No chat history available]';
    }

    return messages
        .map((msg) => {
            const role = msg.role === 'user' ? 'User' : 'Agent';
            const content = typeof msg.content === 'string' ? msg.content : JSON.stringify(msg.content);
            return `${role}: ${content}`;
        })
        .join('\n\n');
}

function formatValue(value: any): string {
    if (value === null || value === undefined) {
        return '';
    }

    if (value instanceof Date) {
        return value.toISOString();
    }

    if (Array.isArray(value)) {
        return value.join(', ');
    }

    if (typeof value === 'object') {
        return JSON.stringify(value);
    }

    if (typeof value === 'boolean') {
        return value ? 'true' : 'false';
    }

    if (typeof value === 'number') {
        return String(value);
    }

    return String(value);
}

/**
 * `{{evalX}}` and `{{testX}}` placeholders alias each other, since JSONB eval* fields often
 * map to test* table columns.
 */
export function interpolatePrompt(
    prompt: string,
    options: PromptInterpolationOptions
): string {
    const { user, chatMessages, log, userId } = options;

    // Pre-process chatHistory if needed (to avoid double replacement)
    let chatHistoryText: string | null = null;
    if (prompt.includes('{{chatHistory}}')) {
        if (chatMessages) {
            try {
                chatHistoryText = formatChatHistory(chatMessages);
            } catch (error) {
                const errorMsg = error instanceof Error ? error.message : String(error);
                if (log && userId) {
                    log.error('prompt-interpolation-chat-history-error', { userId, error: errorMsg });
                }
                chatHistoryText = '[Error loading chat history]';
            }
        } else {
            chatHistoryText = '[Chat history not available]';
        }
    }

    return prompt.replace(/\{\{(\w+)\}\}/g, (match, placeholderFieldName) => {
        if (placeholderFieldName === 'chatHistory') {
            return chatHistoryText || '[Chat history not available]';
        }

        if (placeholderFieldName === 'now') {
            return new Date().toISOString();
        }

        let value: any = null;

        const fieldNamesToTry = [placeholderFieldName];

        if (placeholderFieldName.startsWith('eval')) {
            const testVariant = 'test' + placeholderFieldName.slice(4);
            fieldNamesToTry.push(testVariant);
        }
        if (placeholderFieldName.startsWith('test')) {
            const evalVariant = 'eval' + placeholderFieldName.slice(4);
            fieldNamesToTry.push(evalVariant);
        }

        for (const fieldName of fieldNamesToTry) {
            if (user.data && typeof user.data === 'object' && fieldName in user.data) {
                value = user.data[fieldName];
                if (value !== null && value !== undefined) break;
            }
            if (fieldName in user) {
                value = (user as any)[fieldName];
                if (value !== null && value !== undefined) break;
            }
        }

        if (value !== null && value !== undefined) {
            return formatValue(value);
        }

        if (log && userId) {
            log.warn('prompt-interpolation-placeholder-not-found', { placeholderFieldName, triedFields: fieldNamesToTry, userId });
        }
        return `[${placeholderFieldName} not available]`;
    });
}


export interface IncludeRef {
    kind: 'prompt' | 'file';
    target: string;
}

const INCLUDE = /\{\{(prompt|file):([A-Za-z0-9_./-]+)\}\}/g;

/**
 * Replaces `{{prompt:type}}` and `{{file:path}}` tags with what `resolve` returns. Runs before
 * user-token interpolation; `{{name}}`-style tokens are left alone.
 */
export function expandIncludes(text: string, resolve: (ref: IncludeRef) => string): string {
    return text.replace(INCLUDE, (_match, kind: IncludeRef['kind'], target: string) => resolve({ kind, target }));
}
